import { useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import { v4 as uuidv4 } from "uuid";
import cloneDeep from "lodash/cloneDeep";
import { PolicyDecisionActionTypes } from "@iddataweb/idw-common-ui/enums";
import { resources } from "@constants/hardcoded-strings";
import { CardContainer } from "@containers/common/card-container/card-container";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { RpServiceConfigurationPolicyDecisionContainer } from "@containers/common/rp-service-configuration-policy-decision-container/rp-service-configuration-policy-decision-container";
import { PolicyDecisionOperationTypes } from "@enums/policy-decision-operation-types";
import { PolicyConfigDecisionComponentResultTypes } from "@enums/policy-config-decision-component-result-type";
import {
    FormattedRpServiceConfigurationAttribute,
    FormattedRpServiceConfigurationPolicyConfig,
    FormattedRpServiceConfigurationPolicyConfigDecision,
    FormattedRpServiceConfigurationPolicyConfigDecisionCondition,
    FormattedRpServiceConfigurationServiceOffering
} from "@idw-types/data/rp-service-configuration";
import {
    AxnAttributeType,
    AxnAttributeTypeField,
    AxnCompany
} from "@idw-types/data/reference-data/axn";
import { AxnRpService } from "@idw-types/data/axn-rp-service";
import { AxnAcquiredAttribute } from "@idw-types/data/reference-data/axn/axn-acquired-attribute";
import { reorderItems } from "@utils/drag-and-drop-util";
import { DecisionHeaderContainer } from "../decision-header-container/decision-header-container";
import { AvailableAssertionStatus } from "@idw-types/data/reference-data/generic";
import { AXNUIContent, AXNUIContentPolicyDecisionRetryMessage } from "@iddataweb/idw-common-ui/types/axnui-content";
import { PolicyDetailErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import { SortableDndItem } from "@containers/common/sortable-dnd-container/sortable-dnd-item/sortable-dnd-item";
import { SortableDndContainer } from "@containers/common/sortable-dnd-container/sortable-dnd-container";
import { DragEndEvent } from "@dnd-kit/core";
import { SimpleRPServiceWorkflow } from "@idw-types/data/reference-data/axn/simple-rp-service-workflow";
import { getDecisionEngineRetryMessageToCopy } from "@utils/policies/policy-util";

type DecisionEngineDecisionsContainerProps = {
    sectionTitle: string;
    decisionType: PolicyDecisionActionTypes;
    policyConfig: FormattedRpServiceConfigurationPolicyConfig;
    policyDecisions: Array<FormattedRpServiceConfigurationPolicyConfigDecision>;
    isDisabled: boolean;
    isUsingThemedUI: boolean;
    axnCompanies: Array<AxnCompany>;
    serviceOrganizationID: string;
    validationErrors?: PolicyDetailErrors;
    axnRpServices: Array<AxnRpService>;
    axnuiContent: AXNUIContent;
    axnAttributeTypes: Array<AxnAttributeType>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    attributes: Array<FormattedRpServiceConfigurationAttribute>;
    serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>;
    acquiredAttributes: Array<FormattedRpServiceConfigurationAttribute>;
    unformattedAcquiredAttributes: Array<AxnAcquiredAttribute>;
    simpleRPServiceWorkflows: Array<SimpleRPServiceWorkflow>;
    axnAvailableAssertionStatuses: Array<AvailableAssertionStatus>;
    rpServiceID: string;
    workflowID: string;
    setupPropAndPolicyUpdate: (newPolicyConfig: FormattedRpServiceConfigurationPolicyConfig, newAXNUIContent: AXNUIContent) => void;
    updatePolicyConfig: (policyConfig: FormattedRpServiceConfigurationPolicyConfig) => void;
};

export const DecisionEngineDecisionsContainer = (props: DecisionEngineDecisionsContainerProps) => {
    const {
        sectionTitle,
        decisionType,
        policyConfig,
        policyDecisions,
        isDisabled,
        isUsingThemedUI,
        validationErrors,
        axnRpServices,
        axnuiContent,
        axnCompanies,
        serviceOrganizationID,
        axnAttributeTypes,
        axnAttributeTypeFields,
        attributes,
        serviceOfferings,
        acquiredAttributes,
        simpleRPServiceWorkflows,
        unformattedAcquiredAttributes,
        axnAvailableAssertionStatuses,
        rpServiceID,
        workflowID,
        setupPropAndPolicyUpdate,
        updatePolicyConfig
    } = props;
    const [dndActiveId, setDndActiveId] = useState(null);
    const updateDecision = (decisionToBeUpdated: FormattedRpServiceConfigurationPolicyConfigDecision) => {
        const newDecisions = policyConfig.decisions.map(decision => decision.uuid === decisionToBeUpdated.uuid ? decisionToBeUpdated : decision);
        updatePolicyConfig({
            ...policyConfig,
            decisions: newDecisions
        })
    };

    const updateAXNUIContentPolicyDecisionRetryMessage = (key: keyof AXNUIContentPolicyDecisionRetryMessage, value: string) => {
        const defaultContentPolicyRetryMessage = {
            retryMessage: value
        };
        const newPropertyValue: AXNUIContent = {
            ...axnuiContent,
            policyDecisionRetryMessage: {
                ...axnuiContent.policyDecisionRetryMessage,
                [key]: {
                    ...defaultContentPolicyRetryMessage
                }
            }
        };
        setupPropAndPolicyUpdate(policyConfig, newPropertyValue);
    };


    const removeDecision = (decisionToBeRemoved: FormattedRpServiceConfigurationPolicyConfigDecision) => {
        const newDecisions: Array<FormattedRpServiceConfigurationPolicyConfigDecision> = policyConfig.decisions.filter(decision => decision.uuid !== decisionToBeRemoved.uuid);
        const newPolicyDecisionRetryMessage = { ...axnuiContent.policyDecisionRetryMessage };
        if (decisionToBeRemoved.id) {
            delete newPolicyDecisionRetryMessage[decisionToBeRemoved.id];
        } else {
            delete newPolicyDecisionRetryMessage[decisionToBeRemoved.uuid];
        }

        const newPropertyValue: AXNUIContent = {
            ...axnuiContent,
            policyDecisionRetryMessage: newPolicyDecisionRetryMessage
        };
        const newPolicyConfig = {
            ...policyConfig,
            decisions: newDecisions
        };
        setupPropAndPolicyUpdate(newPolicyConfig, newPropertyValue);
    };

    const copyDecision = (decisionToBeCopied: FormattedRpServiceConfigurationPolicyConfigDecision) => {
        const newDecisionUuid = uuidv4();
        const newDecisions: Array<FormattedRpServiceConfigurationPolicyConfigDecision> = [...policyConfig.decisions, {
            ...decisionToBeCopied,
            id: null,
            conditions: decisionToBeCopied.conditions.map(condition => {
                const newCondition: FormattedRpServiceConfigurationPolicyConfigDecisionCondition = {
                    ...condition,
                    id: null,
                    uuid: uuidv4()
                };
                return newCondition;
            }),
            uuid: newDecisionUuid
        }];

        const newPolicyDecisionRetryMessage = { ...axnuiContent.policyDecisionRetryMessage };
        const retryMessageToCopy = getDecisionEngineRetryMessageToCopy(decisionToBeCopied.id, decisionToBeCopied.uuid, axnuiContent.policyDecisionRetryMessage);

        newPolicyDecisionRetryMessage[newDecisionUuid] = { retryMessage: retryMessageToCopy };

        const newPropertyValue: AXNUIContent = {
            ...axnuiContent,
            policyDecisionRetryMessage: newPolicyDecisionRetryMessage
        };
        const newPolicyConfig = {
            ...policyConfig,
            decisions: newDecisions
        };
        setupPropAndPolicyUpdate(newPolicyConfig, newPropertyValue);

    };

    const copyOppositeDecision = (decisionToBeCopied: FormattedRpServiceConfigurationPolicyConfigDecision) => {
        const newConditions: Array<FormattedRpServiceConfigurationPolicyConfigDecisionCondition> = decisionToBeCopied.conditions.map(condition => {
            let values = null;
            if (condition.component) {
                values = condition.values === PolicyConfigDecisionComponentResultTypes.PASS
                    ? PolicyConfigDecisionComponentResultTypes.FAIL
                    : PolicyConfigDecisionComponentResultTypes.PASS;
            } else {
                values = condition.values;
            }

            return {
                ...condition,
                id: null,
                values,
                uuid: uuidv4()
            } as FormattedRpServiceConfigurationPolicyConfigDecisionCondition;
        });

        const newDecisions: Array<FormattedRpServiceConfigurationPolicyConfigDecision> = [...policyConfig.decisions, {
            ...decisionToBeCopied,
            id: null,
            conditions: newConditions,
            policyDecisionOperationType: decisionToBeCopied.policyDecisionOperationType === PolicyDecisionOperationTypes.OR
                ? PolicyDecisionOperationTypes.AND
                : PolicyDecisionOperationTypes.OR,
            policyDecisionType: [PolicyDecisionActionTypes.DENY, PolicyDecisionActionTypes.OBLIGATION].includes(decisionToBeCopied.policyDecisionType)
                ? PolicyDecisionActionTypes.APPROVE
                : PolicyDecisionActionTypes.DENY,
            retries: [PolicyDecisionActionTypes.DENY, PolicyDecisionActionTypes.OBLIGATION].includes(decisionToBeCopied.policyDecisionType)
                ? decisionToBeCopied.retries
                : "0",
            uuid: uuidv4()
        }];

        updatePolicyConfig({
            ...policyConfig,
            decisions: newDecisions
        });
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            const oldIndex = active.data.current.sortable.index;
            const newIndex = over.data.current.sortable.index;

            const currentDecisions = cloneDeep(policyDecisions);
            const updatedDecisions: Array<FormattedRpServiceConfigurationPolicyConfigDecision> = reorderItems(
                currentDecisions, oldIndex, newIndex
            );

            const updatedDecisionUUIDs = updatedDecisions.map(ud => ud.uuid);

            const finalDecisions = cloneDeep(policyConfig.decisions.filter(decision => !updatedDecisionUUIDs.includes(decision.uuid)));
            updatedDecisions.forEach(up => {
                const nulledOutDecision: FormattedRpServiceConfigurationPolicyConfigDecision = {
                    ...up,
                    id: null,
                    conditions: up.conditions.map(condition => {
                        const newCondition: FormattedRpServiceConfigurationPolicyConfigDecisionCondition = {
                            ...condition,
                            id: null
                        };
                        return newCondition;
                    })
                };
                finalDecisions.push(nulledOutDecision);
            });

            updatePolicyConfig({
                ...policyConfig,
                decisions: finalDecisions
            })
        }
    };


    const policyDecisionValues = useMemo(() => {
        return policyDecisions.map(decision => decision.uuid);
    }, [policyDecisions]);

    const policyDecisionElements: Array<(shouldApplyPlaceholder: boolean, activeId?: string) => React.ReactNode> = useMemo(() => {
        return policyDecisions.map((decision, index) => (
            (shouldApplyPlaceholder: boolean, activeId: string) => (
                <SortableDndItem
                    key={`${decisionType}-decision-${decision.uuid}`}
                    isActiveId={shouldApplyPlaceholder && activeId === decision.uuid}
                    showDragAndDropIcon={false}
                    id={decision.uuid}
                    additionalStyle={{}}
                    activeIdStyle={{}}
                >
                    <CardContainer key={`policy-config-${decisionType}-decision-${decision.uuid}`}>
                        <div>
                            <DecisionHeaderContainer
                                isDisabled={isDisabled}
                                showDragAndDropIndicatorIcon={true}
                                decision={decision}
                                title={`Custom Rule #${index + 1}`}
                                copyDecision={copyDecision}
                                copyOppositeDecision={copyOppositeDecision}
                                removeDecision={removeDecision}
                            />
                        </div>
                        <RpServiceConfigurationPolicyDecisionContainer
                            isDisabled={isDisabled}
                            isUsingThemedUI={isUsingThemedUI}
                            axnCompanies={axnCompanies}
                            serviceOrganizationID={serviceOrganizationID}
                            axnRpServices={axnRpServices}
                            attributes={attributes}
                            axnuiContent={axnuiContent}
                            validationErrors={validationErrors}
                            acquiredAttributes={acquiredAttributes}
                            decision={decision}
                            policyRetries={policyConfig.policyRetries}
                            serviceOfferings={serviceOfferings}
                            axnAttributeTypes={axnAttributeTypes}
                            axnAttributeTypeFields={axnAttributeTypeFields}
                            unformattedAcquiredAttributes={unformattedAcquiredAttributes}
                            simpleRPServiceWorkflows={simpleRPServiceWorkflows}
                            axnAvailableAssertionStatuses={axnAvailableAssertionStatuses}
                            rpServiceID={rpServiceID}
                            workflowID={workflowID}
                            updateAXNUIContentPolicyDecisionRetryMessage={updateAXNUIContentPolicyDecisionRetryMessage}
                            setDecision={updateDecision}
                        />
                    </CardContainer></SortableDndItem>)));
    }, [
        policyDecisions, dndActiveId, isDisabled, isUsingThemedUI,
        axnCompanies, axnRpServices, attributes, axnuiContent,
        validationErrors, acquiredAttributes, policyConfig.policyRetries,
        serviceOfferings, axnAttributeTypes, unformattedAcquiredAttributes,
        simpleRPServiceWorkflows, axnAvailableAssertionStatuses,
        rpServiceID, workflowID
    ]);

    return (
        <CardContainer>
            <ContentPageHeader title={sectionTitle} />
            <ContentPageBody isMaxWidth={true}>
                {policyDecisions.length > 0 ? (
                    <SortableDndContainer
                        isDisabled={isDisabled}
                        internalDragAndDropElements={policyDecisionElements}
                        internalDragAndDropElementsValues={policyDecisionValues}
                        handleDragEnd={handleDragEnd}
                        activeId={dndActiveId}
                        setActiveId={setDndActiveId}
                    />
                ) : (
                    <Alert variant={"info"}>{resources.pages.verificationServiceDetails.tabs.policies.sections.editor.sections.rules.noDecisionActionTypeRulesMessage(decisionType)}</Alert>
                )}
            </ContentPageBody>
        </CardContainer>
    );
};